import * as React from 'react'
import { Users as UsersIcon } from 'lucide-react'
import type {
  PlantLineCrewRequirement,
  PlantLineOperations
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { SectionHeader } from './entity/SectionHeader'
import { formatCrewCapacity, formatOperationKind, formatRoleChipLabel } from './labor-format'
import { PlantHref } from './PlantHref'

/**
 * Crew capacity panel for a Line page. Operation identity and resources
 * live in OperationWorkSections; this section only surfaces shared
 * CrewRequirement capacity.
 */
export function LaborStaffingSection({
  labor
}: {
  labor: PlantLineOperations | undefined
}): React.ReactElement | null {
  if (!labor) return null
  if (labor.crewRequirements.length === 0) return null

  const crewCount = labor.crewRequirements.length

  return (
    <section>
      <SectionHeader
        label="Crew capacity"
        subtitle={`${crewCount} ${crewCount === 1 ? 'crew' : 'crews'}`}
      />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-3">
          {labor.crewRequirements.map((crew) => (
            <CrewRow key={crew.crew.id} crew={crew} />
          ))}
        </div>
      </div>
    </section>
  )
}

function CrewRow({ crew }: { crew: PlantLineCrewRequirement }): React.ReactElement {
  const capacity = formatCrewCapacity(crew.crew.headcount)

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <PlantHref
          id={crew.crew.id}
          label="CrewRequirement"
          node={crew.crew}
          className="inline-flex items-center gap-1.5 text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
        >
          <UsersIcon size={13} className="text-ink-3" aria-hidden />
          {crew.crew.name}
        </PlantHref>
        {capacity && (
          <Badge variant="outline" className="text-10">
            {capacity}
          </Badge>
        )}
      </div>

      {(crew.staffing.length > 0 || crew.coveredOperations.length > 0) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {crew.staffing.map((row) => (
            <PlantHref
              key={row.role.id}
              id={row.role.id}
              label="Role"
              node={row.role}
              className="inline-flex items-center rounded-full border border-sky-400/40 bg-sky-500/10 px-2 py-0.5 text-11 font-medium text-sky-700 no-underline hover:bg-sky-500/20 dark:text-sky-300"
              title={row.role.role_kind ? `Role · ${row.role.role_kind}` : row.role.name}
            >
              {formatRoleChipLabel(row.role.name, row.minGrade, row.maxGrade)}
            </PlantHref>
          ))}
          {crew.coveredOperations.map((op) => (
            <PlantHref
              key={op.id}
              id={op.id}
              label="HumanLabor"
              node={op}
              className="inline-flex items-center rounded-full border border-border bg-bg-sunk px-2 py-0.5 text-11 font-medium text-ink-2 no-underline hover:bg-accent"
              title={formatOperationKind(op.operation_kind) ?? op.name}
            >
              covers {op.name}
            </PlantHref>
          ))}
        </div>
      )}

      {crew.crew.description && (
        <div className="mt-0.5 text-12 text-ink-3">{crew.crew.description}</div>
      )}
    </div>
  )
}
